import { useLang } from "@/i18n/LanguageContext";
import { useSettings } from "@/lib/useSettings";
import { Link } from "react-router-dom";
import { ArrowRight, Linkedin } from "lucide-react";
import SeoHead, { SITE_URL } from "@/components/SeoHead";
import PageHero from "@/components/PageHero";
import { SectionBand, SectionHeader } from "@/components/SectionPrimitives";
import { buildOrganizationSchema, buildPersonSchema } from "@/lib/schema";
import { BRAND } from "@/lib/dataleo";
import { EDITORIAL_PHOTOS } from "@/lib/editorialPhotos";

const About = () => {
  const { lang } = useLang();
  const { settings } = useSettings();

  const copy = lang === "fr" ? {
    eyebrow: "À propos de Dataleo",
    title: "Un partenaire client-side spécialisé en Supply Chain Decision Systems.",
    lead: "Dataleo est un boutique consulting & technology partner spécialisé dans les systèmes de décision Supply Chain - processus, technologie d'entreprise, data et IA.",
    heroCaption: "Decision room · Dataleo",
    body1: "Nous aidons les entreprises à concevoir, livrer, gouverner et faire évoluer leurs décisions Supply Chain - de la stratégie au RUN. Nous intervenons client-side, entre le business, l'IT, les éditeurs et les intégrateurs.",
    pull: BRAND.editorial.fr,
    body2: "Notre rôle n'est pas de remplacer l'intégrateur. C'est de s'assurer que l'ensemble de la transformation fonctionne pour le client.",
    valuesKicker: "Nos valeurs de pratique",
    valuesTitle: "Boutique, mais exigeant.",
    valuesList: [
      { k: "Client-side", v: "Nous travaillons POUR le client, pas contre son intégrateur." },
      { k: "Senior", v: "Chaque mission est portée par un profil senior, impliqué dans la décision." },
      { k: "Boutique", v: "Petites équipes, cadrage net, pas de pyramide junior." },
      { k: "Système", v: "Pas une mission ponctuelle : un système de décision qui vit après go-live." },
    ],
    founderKicker: "Fondateur & Principal",
    founderName: "Jean-François Nordmann",
    founderBio: "Expérience en management Supply Chain, transformation planning, APS, delivery de programmes globaux et architecture de systèmes de décision. Contributeur actif sur les sujets IA / data en Supply Chain.",
    primaryCta: "En parler avec Dataleo",
    secondaryCta: "Nos capacités",
  } : {
    eyebrow: "About Dataleo",
    title: "A client-side partner specialised in Supply Chain Decision Systems.",
    lead: "Dataleo is a boutique consulting & technology partner specialised in Supply Chain decision systems - processes, enterprise technology, data and AI.",
    heroCaption: "Decision room · Dataleo",
    body1: "We help companies design, deliver, govern and continuously improve their Supply Chain decisions - from strategy to RUN. We operate client-side, across business, IT, software vendors and system integrators.",
    pull: BRAND.editorial.en,
    body2: "Our role is not to replace the system integrator. It is to make sure the overall transformation works for the client.",
    valuesKicker: "How we practice",
    valuesTitle: "Boutique, demanding.",
    valuesList: [
      { k: "Client-side", v: "We work FOR the client, not against their system integrator." },
      { k: "Senior", v: "Every engagement is led by a senior who stays close to the decision." },
      { k: "Boutique", v: "Small teams, sharp framing, no junior pyramid." },
      { k: "System", v: "Not a one-off engagement: a decision system that lives after go-live." },
    ],
    founderKicker: "Founder & Principal",
    founderName: "Jean-François Nordmann",
    founderBio: "Experience across Supply Chain leadership, planning transformation, APS, global program delivery and decision-system architecture. Active contributor on AI / data topics in Supply Chain.",
    primaryCta: "Talk to Dataleo",
    secondaryCta: "Our capabilities",
  };

  const linkedinJfn = settings.linkedin_jfn_url || "https://www.linkedin.com/in/jf-nordmann/";

  return (
    <div data-testid="about-page">
      <SeoHead
        lang={lang}
        path="/about"
        title={lang === "fr" ? "À propos - Dataleo" : "About - Dataleo"}
        description={copy.lead}
        jsonLd={[
          buildOrganizationSchema({ baseUrl: SITE_URL, settings }),
          buildPersonSchema({ baseUrl: SITE_URL, settings }),
        ]}
      />
      <PageHero
        tone="cream"
        kicker={copy.eyebrow}
        title={copy.title}
        description={copy.lead}

        testId="about-hero"
      />

      {/* Body - single editorial column with a pull quote. */}
      <SectionBand tone="cream">
        <div className="max-w-3xl">
          <p className="text-[18px] sm:text-[20px] leading-[1.65] text-[#0B1210]">
            {copy.body1}
          </p>

          <blockquote className="my-14 sm:my-16 border-l-4 border-[#FFD400] pl-7 py-3 font-display italic text-3xl sm:text-4xl text-[#0B1210] leading-[1.1]" style={{ fontWeight: 900 }}>
            "{copy.pull}"
          </blockquote>

          <p className="text-[18px] sm:text-[20px] leading-[1.65] text-[#0B1210]">
            {copy.body2}
          </p>
        </div>
      </SectionBand>

      {/* Values - dark alternating band. */}
      <SectionBand tone="dark" testId="about-values">
        <SectionHeader kicker={copy.valuesKicker} title={copy.valuesTitle} tone="dark" />
        <div className="mt-14 grid sm:grid-cols-2 gap-6">
          {copy.valuesList.map((v, i) => (
            <div key={i} data-testid={`about-value-${i}`} className="bg-white text-[#0B1210] rounded-[24px] p-7">
              <div className="font-mono-dl text-[11px] tracking-[0.22em] uppercase text-[#00A878]">
                0{i + 1}
              </div>
              <h3 className="font-display text-2xl sm:text-3xl mt-3 leading-tight">
                {v.k}
              </h3>
              <p className="mt-3 text-[14.5px] text-[#4B5563] leading-relaxed">
                {v.v}
              </p>
            </div>
          ))}
        </div>
      </SectionBand>

      {/* Founder - slim byline strip. */}
      <SectionBand tone="cream" testId="about-founder">
        <div className="max-w-3xl">
          <div className="font-mono-dl text-[11px] tracking-[0.22em] uppercase text-[#00A878]">
            {copy.founderKicker}
          </div>
          <div className="mt-4 flex flex-wrap items-baseline gap-x-5 gap-y-2">
            <h3 className="font-display text-4xl sm:text-5xl text-[#0B1210] leading-tight">
              {copy.founderName}
            </h3>
            <a
              href={linkedinJfn}
              target="_blank"
              rel="noopener noreferrer me"
              className="inline-flex items-center gap-1.5 text-[13px] font-bold-dl text-[#0B1210] hover:text-[#00A878] min-h-[40px]"
            >
              <Linkedin size={14} /> LinkedIn
            </a>
          </div>
          <p className="mt-5 text-[16px] leading-relaxed text-[#4B5563] max-w-2xl">
            {copy.founderBio}
          </p>
        </div>
      </SectionBand>

      {/* Bottom CTA - cream alt. */}
      <SectionBand tone="cream" className="bg-[#EFEAE0]">
        <div className="flex flex-wrap items-center gap-3">
          <Link to="/contact" data-testid="about-cta-contact" className="dl-pill dl-pill-primary">
            {copy.primaryCta} <ArrowRight size={15} strokeWidth={2.5} />
          </Link>
          <Link to="/capabilities" data-testid="about-cta-capabilities" className="dl-pill dl-pill-outline">
            {copy.secondaryCta} <ArrowRight size={15} strokeWidth={2.5} />
          </Link>
        </div>
      </SectionBand>
    </div>
  );
};

export default About;
